import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { BaseFieldComponent } from '../base-field.component';
import { User } from '../../../../../models/user.model';

export interface CrewMemberValue {
    userId: number | null;
    earlyShift: boolean;
}

@Component({
    selector: 'app-crew-member-field',
    imports: [IonicModule, FormsModule],
    template: `
        @if (readonly) {
            <ion-item lines="none">
                <ion-label>
                    <p class="field-label">{{ field.label }}</p>
                    <p class="field-value">
                        {{ getSelectedUserName() }}
                        @if (compositeValue.earlyShift) {
                            <ion-badge color="secondary" class="early-shift-badge">Opstart</ion-badge>
                        }
                    </p>
                </ion-label>
            </ion-item>
        } @else {
            <ion-grid class="crew-member-grid">
                <ion-row class="crew-member-header">
                    <ion-col class="crew-member-label">
                        {{ field.label }}
                        @if (isRequired) {
                            <span class="required-indicator">*</span>
                        }
                    </ion-col>
                    <ion-col size="auto" class="crew-member-opstart-label">
                        Opstart
                    </ion-col>
                </ion-row>
                <ion-row class="crew-member-form">
                    <ion-col class="crew-member-select">
                        <ion-select
                            [placeholder]="field.placeholder || 'Selecteer...'"
                            [value]="selectedUser"
                            [compareWith]="compareUsers"
                            [interfaceOptions]="{ cssClass: 'action-sheet-eventslight' }"
                            interface="action-sheet"
                            (ionChange)="onUserChange($event)">
                            @for (user of sortedUsers; track user.id) {
                                <ion-select-option [value]="user">
                                    {{ getUserDisplayName(user) }}
                                </ion-select-option>
                            }
                        </ion-select>
                    </ion-col>
                    <ion-col size="auto" class="crew-member-checkbox">
                        <ion-checkbox
                            [checked]="compositeValue.earlyShift"
                            color="secondary"
                            mode="ios"
                            (ionChange)="onEarlyShiftChange($event)">
                        </ion-checkbox>
                    </ion-col>
                </ion-row>
            </ion-grid>
            @if (field.helpText) {
                <ion-note class="field-help">{{ field.helpText }}</ion-note>
            }
        }
    `,
    styles: [`
        .crew-member-grid {
            padding: 8px 16px;
        }

        .crew-member-grid ion-col {
            padding: 0;
            margin: 0;
        }

        .crew-member-header {
            margin-bottom: 4px;
        }

        .crew-member-label {
            font-size: 14px;
            font-weight: 500;
            color: var(--ion-color-dark);
        }

        .crew-member-opstart-label {
            font-size: 14px;
            font-weight: 500;
            color: var(--ion-color-dark);
            padding-right: 8px;
        }

        .crew-member-form {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
        }

        .crew-member-select {
            flex: 1;
        }

        .crew-member-select ion-select {
            background-color: #edeef0;
            border-radius: 5px;
            width: 100%;
            max-width: 100%;
        }

        .crew-member-checkbox {
            display: flex;
            align-items: center;
            justify-content: center;
            padding-left: 12px;
        }

        .field-help {
            padding: 4px 16px;
            font-size: 12px;
        }

        .field-label {
            color: var(--ion-color-medium);
            font-size: 12px;
        }

        .field-value {
            white-space: pre-wrap;
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .early-shift-badge {
            font-size: 10px;
            padding: 2px 6px;
        }

        .required-indicator {
            color: var(--ion-color-danger);
            margin-left: 2px;
        }
    `]
})
export class CrewMemberFieldComponent extends BaseFieldComponent {
    compositeValue: CrewMemberValue = { userId: null, earlyShift: false };
    selectedUser: User | null = null;

    get sortedUsers(): User[] {
        return [...this.users].sort((a, b) =>
            (a.firstName ?? '').localeCompare(b.firstName ?? '')
        );
    }

    initialize(value: any): void {
        if (value === null || value === undefined) {
            this.compositeValue = { userId: null, earlyShift: false };
            this.selectedUser = null;
            return;
        }

        // Handle JSON string from API
        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                this.compositeValue = {
                    userId: parsed.userId ?? null,
                    earlyShift: parsed.earlyShift ?? false
                };
            } catch {
                // If parsing fails, try to interpret as user ID
                const userId = parseInt(value, 10);
                this.compositeValue = {
                    userId: isNaN(userId) ? null : userId,
                    earlyShift: false
                };
            }
        } else if (typeof value === 'object') {
            this.compositeValue = {
                userId: value.userId ?? null,
                earlyShift: value.earlyShift ?? false
            };
        } else if (typeof value === 'number') {
            // Just a user ID was provided (from dynamic default value resolver)
            this.compositeValue = {
                userId: value,
                earlyShift: false
            };
        }

        // Find the selected user object
        if (this.compositeValue.userId !== null) {
            this.selectedUser = this.users.find(u => u.id === this.compositeValue.userId) ?? null;
        }
    }

    serialize(): any {
        return this.compositeValue;
    }

    onUserChange(event: any): void {
        const user = event.detail.value as User;
        this.selectedUser = user;
        this.compositeValue.userId = user?.id ?? null;
        this.emitChange(this.compositeValue);
    }

    onEarlyShiftChange(event: any): void {
        this.compositeValue.earlyShift = event.detail.checked;
        this.emitChange(this.compositeValue);
    }

    compareUsers(user1: User, user2: User): boolean {
        return user1 && user2 ? user1.id === user2.id : user1 === user2;
    }

    getUserDisplayName(user: User): string {
        if (!user) { return ''; }
        return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || '';
    }

    getSelectedUserName(): string {
        if (!this.selectedUser && this.compositeValue.userId === null) {
            return '-';
        }
        if (this.selectedUser) {
            return this.getUserDisplayName(this.selectedUser);
        }
        // Try to find user by ID
        const user = this.users.find(u => u.id === this.compositeValue.userId);
        return user ? this.getUserDisplayName(user) : '-';
    }
}
